import React from 'react';
import {
  Checkbox,
  ColorValues,
  Flex,
  Spinner,
  Typography,
} from '@banhala/ui-kit';
import { useAtom } from 'jotai';
import { marketGoodsListFilterAtom } from '../../../../states/GoodsListFilter.atom';
import { isNil, localizeNumber } from 'shared/src/utils';
import { useLogClickHeadlineXsmallOneLineToggle } from './hooks/useLogClickHeadlineXsmallOneLineToggle';
import { DeliveryType } from 'shared/src/enums';
import { useMarketHomeGoods } from '../../../../hooks/useMarketHomeGoods';
import { ErrorBoundary } from 'shared/src/component';
import {
  BrandIconThirdPlDelivery,
  BrandIconTodayShipping,
} from 'shared/src/component/BrandIcon';

const GoodsCountHeaderContent = ({ marketId }: { marketId: number }) => {
  const [filter, setFilter] = useAtom(marketGoodsListFilterAtom);
  const {
    totalCount,
    hasTodayGoods,
    hasShakGoods,
    hasThirdPlGoods,
    listId,
    isLoading,
  } = useMarketHomeGoods(marketId, filter[marketId]);

  const logClickTodayCheckbox = useLogClickHeadlineXsmallOneLineToggle({
    listId,
    toggleType: 'today',
  });
  const logClickThirdPlCheckbox = useLogClickHeadlineXsmallOneLineToggle({
    listId,
    toggleType: 'third_pl',
  });

  const isThirdPlChecked =
    filter[marketId].deliveryType === DeliveryType.ThirdPl;
  const isFastDeliveryChecked =
    filter[marketId].deliveryType === DeliveryType.Fast;

  const setDeliveryType = (deliveryType: DeliveryType | null) => {
    setFilter(prev => ({
      ...prev,
      [marketId]: {
        ...prev[marketId],
        deliveryType,
      },
    }));
  };

  // 체크박스는 하나만 선택 가능 — 한쪽을 켜면 다른 쪽은 해제된다
  const handlePressThirdPl = () => {
    const next = !isThirdPlChecked;
    logClickThirdPlCheckbox(next);
    setDeliveryType(next ? DeliveryType.ThirdPl : null);
  };

  const handlePressFastDelivery = () => {
    const next = !isFastDeliveryChecked;
    logClickTodayCheckbox(next);
    setDeliveryType(next ? DeliveryType.Fast : null);
  };

  const showThirdPlCheckbox = hasThirdPlGoods;
  const showFastDeliveryCheckbox = hasTodayGoods || hasShakGoods;

  if (isLoading) return <GoodsCountHeaderSkeleton />;

  if (isNil(listId)) return null;

  return (
    <Flex.Row
      padding={{ horizontal: 12, vertical: 14 }}
      alignItems={'center'}
      justifyContent={'space-between'}
      backgroundColor={'white'}
      height={48}
    >
      <Flex.Row gap={4} alignItems={'center'}>
        <Typography type={'subtitle1'} color={'content_primary'}>
          상품
        </Typography>
        <Typography type={'subtitle1'} color={'content_secondary'}>
          {localizeNumber(totalCount)}
        </Typography>
      </Flex.Row>
      <Flex.Row gap={12} alignItems={'center'}>
        {showThirdPlCheckbox && (
          <Flex.Row gap={4} alignItems={'center'}>
            <Flex
              testID={`도착보장체크박스?value=${isThirdPlChecked ? '1' : '0'}`}
            >
              <Checkbox value={isThirdPlChecked} onPress={handlePressThirdPl} />
            </Flex>
            <BrandIconThirdPlDelivery
              width={49}
              height={14}
              color={ColorValues.pink30}
            />
          </Flex.Row>
        )}
        {showFastDeliveryCheckbox && (
          <Flex.Row gap={4} alignItems={'center'}>
            <Flex
              testID={`오늘출발체크박스?value=${
                isFastDeliveryChecked ? '1' : '0'
              }`}
            >
              <Checkbox
                value={isFastDeliveryChecked}
                onPress={handlePressFastDelivery}
              />
            </Flex>
            <BrandIconTodayShipping
              width={49}
              height={14}
              color={ColorValues.lilac40}
            />
          </Flex.Row>
        )}
      </Flex.Row>
    </Flex.Row>
  );
};

const GoodsCountHeaderSkeleton = () => {
  return (
    <Flex.Row
      padding={{ horizontal: 12 }}
      alignItems={'center'}
      justifyContent={'space-between'}
      backgroundColor={'white'}
      height={48}
    >
      <Flex.Row gap={12} alignItems={'center'}>
        <Typography type={'subtitle1'} color={'content_primary'}>
          상품
        </Typography>
        <Spinner size={8} color={'content_tertiary'} />
      </Flex.Row>

      <Flex
        backgroundColor={'bg_tertiary'}
        width={50}
        height={14}
        borderRadius={4}
      />
    </Flex.Row>
  );
};

export const GoodsCountHeader = ({ marketId }: { marketId: number }) => {
  return (
    // 에러 시 상품 개수 안 보여주기
    <ErrorBoundary ErrorFallback={() => <></>}>
      <GoodsCountHeaderContent marketId={marketId} />
    </ErrorBoundary>
  );
};
